import { useState } from "react";
import Abas from "../../../components/Abas";
import Esqueleto from "../../../components/Esqueleto";
import TabelaAdmin from "../../../components/TabelaAdmin";
import { Link } from "react-router-dom";
import ReactApexChart from "react-apexcharts";
import Relatorio from "../../../../models/Relatorio";
import { COR_GRADE, COR_PAPEL, COR_SERIE, COR_SUPERFICIE, COR_TEXTO, Filtros, Kpis, Secao } from "../../Relatorios/painel";
import { Kpi } from "../../Relatorios/ResumoKpis";
import { numero, useRelatorio } from "../../Relatorios/useRelatorio";
import { Atalhos, BotaoLink, Destaque, Graficos, Recentes } from "./styled";

const dataCurta = (iso) => (iso ? new Date(iso).toLocaleDateString("pt-BR") : "");

const localDe = (cidade, estado) => [cidade, estado].filter(Boolean).join(" / ") || "Local não informado";

// ---------------------------------------------------------------- Pendências

// O que exige ação do administrador vem primeiro. `resumo` é a consulta única
// que o Painel faz a /relatorios/resumo.
export function BlocoPendentes({ resumo }) {
  const { dados, erro, carregando } = resumo;
  if (carregando && !dados) return <Destaque><Esqueleto variante="texto" quantidade={1} rotulo="Verificando pedidos" /></Destaque>;
  if (erro) return <Destaque role="alert"><span className="erro">Não foi possível verificar os pedidos: {erro}</span></Destaque>;

  // Três tipos de pedido: perfil Profissional, apoiador e patrocinador. Resumos antigos
  // (sem os blocos novos) valem 0 para eles.
  const perfil = dados?.solicitacoes_profissional?.pendentes || 0;
  const apoio = dados?.solicitacoes_apoiador?.pendentes || 0;
  const patrocinio = dados?.solicitacoes_patrocinador?.pendentes || 0;
  const total = perfil + apoio + patrocinio;

  if (total === 0) {
    return (
      <Destaque $atencao={false}>
        <div className="texto">
          <span className="titulo">Nenhum pedido aguardando</span>
          <span className="detalhe">Quando alguém pedir o perfil Profissional, para ser apoiador ou para patrocinar, o pedido aparece aqui.</span>
        </div>
        <Link to="/admin/pedidos">Ver histórico de pedidos</Link>
      </Destaque>
    );
  }

  return (
    <Destaque $atencao role="status">
      <div className="texto">
        <span className="titulo">
          {total === 1 ? "1 pedido aguardando resposta" : `${numero(total)} pedidos aguardando resposta`}
        </span>
        <span className="detalhe">
          Perfil Profissional: {numero(perfil)} · Apoiador: {numero(apoio)} · Patrocinador: {numero(patrocinio)}. Recusar exige informar o motivo.
        </span>
      </div>
      <div className="botoes">
        {perfil > 0 && <BotaoLink to="/admin/pedidos?tipo=profissional">Responder pedidos de perfil ({numero(perfil)})</BotaoLink>}
        {apoio > 0 && <BotaoLink to="/admin/pedidos?tipo=apoiador">Responder pedidos de apoio ({numero(apoio)})</BotaoLink>}
        {patrocinio > 0 && <BotaoLink to="/admin/pedidos?tipo=patrocinador">Responder pedidos de patrocínio ({numero(patrocinio)})</BotaoLink>}
      </div>
    </Destaque>
  );
}

// ------------------------------------------------------------------- Atalhos

export function BlocoAtalhos() {
  return (
    <Atalhos aria-label="Atalhos do painel">
      <Link to="/admin/relatorios"><strong>Relatórios</strong><span>Plantios e usuários por região, estado e cidade</span></Link>
      <Link to="/admin/usuarios"><strong>Usuários</strong><span>Buscar, promover, rebaixar, ativar e desativar</span></Link>
      <Link to="/admin/pedidos"><strong>Pedidos</strong><span>Perfil Profissional, apoiadores e patrocinadores</span></Link>
      <Link to="/admin/conteudo"><strong>Conteúdo</strong><span>Espécies, biomas, banners e apoiadores</span></Link>
    </Atalhos>
  );
}

// ----------------------------------------------------------------- Crescimento

// Uma série por gráfico, no mesmo formato e na mesma largura: usuários e
// plantios são contagens por mês, mas de ordens de grandeza diferentes, e juntar
// os dois num eixo esconderia o menor. Pequenos múltiplos, nunca eixo duplo.
function GraficoMensal({ titulo, unidade, itens, campo, cor }) {
  const valores = itens.map((i) => i[campo] || 0);
  const maximo = Math.max(4, ...valores);

  const options = {
    chart: { type: "line", height: 220, toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false }, fontFamily: "inherit" },
    colors: [cor],
    stroke: { width: 2, curve: "straight" },
    // Marca de 8px de diâmetro, com anel da cor da superfície onde se cruza com a linha.
    markers: { size: 4, strokeWidth: 2, strokeColors: COR_SUPERFICIE, hover: { size: 5 } },
    dataLabels: { enabled: false },
    legend: { show: false },
    grid: { borderColor: COR_GRADE, strokeDashArray: 0, xaxis: { lines: { show: false } }, yaxis: { lines: { show: true } } },
    xaxis: {
      categories: itens.map((i) => i.label),
      axisBorder: { show: false },
      axisTicks: { show: false },
      labels: { style: { colors: COR_TEXTO, fontSize: "11px" } },
    },
    yaxis: {
      min: 0,
      max: maximo,
      tickAmount: 4,
      labels: { formatter: (v) => numero(Math.round(v)), style: { colors: COR_TEXTO, fontSize: "11px" } },
    },
    tooltip: { y: { formatter: (v) => `${numero(v)} ${unidade}` } },
  };

  return (
    <div className="grafico">
      <h4>{titulo}</h4>
      <ReactApexChart type="line" height={220} options={options} series={[{ name: titulo, data: valores }]} />
    </div>
  );
}

export function BlocoCrescimento() {
  const [meses, setMeses] = useState(6);
  const { dados, erro, carregando } = useRelatorio(Relatorio.crescimento, { meses });
  const itens = dados?.itens || [];

  return (
    <Secao aria-labelledby="titulo-crescimento">
      <div>
        <h3 id="titulo-crescimento">Crescimento da plataforma</h3>
        <p className="sub">Usuários novos e plantios registrados em cada mês.</p>
      </div>

      <Filtros>
        <div className="segmentado" role="group" aria-label="Período do gráfico">
          {[6, 12].map((m) => (
            <button key={m} type="button" aria-pressed={meses === m} onClick={() => setMeses(m)}>Últimos {m} meses</button>
          ))}
        </div>
      </Filtros>

      {carregando && !dados && <Esqueleto variante="texto" quantidade={3} rotulo="Carregando gráfico" />}
      {erro && <div className="erro" role="alert">{erro}</div>}

      {itens.length > 0 &&
        <Graficos>
          <GraficoMensal titulo="Usuários novos por mês" unidade="usuários" itens={itens} campo="usuarios" cor={COR_PAPEL.Comum} />
          <GraficoMensal titulo="Plantios por mês" unidade="plantios" itens={itens} campo="plantios" cor={COR_SERIE} />
          <details style={{ gridColumn: "1 / -1" }}>
            <summary>Ver como tabela</summary>
            <table>
              <caption className="sr-only" style={{ position: "absolute", left: "-9999px" }}>Crescimento mensal</caption>
              <thead><tr><th scope="col">Mês</th><th scope="col">Usuários novos</th><th scope="col">Plantios</th><th scope="col">Mudas</th></tr></thead>
              <tbody>
                {itens.map((i) => (
                  <tr key={i.mes}><th scope="row">{i.label}</th><td>{numero(i.usuarios)}</td><td>{numero(i.plantios)}</td><td>{numero(i.mudas)}</td></tr>
                ))}
              </tbody>
            </table>
          </details>
        </Graficos>
      }
    </Secao>
  );
}

// ------------------------------------------------------------------ Plataforma

// Contagens de conteúdo e comunidade. Qualquer uma pode vir null (o backend
// isola a falha de cada consulta): aparece como "—", não como "0" enganoso.
export function BlocoPlataforma({ resumo }) {
  const { dados, erro, carregando } = resumo;
  if (carregando && !dados) return null;
  if (erro || !dados?.plataforma) return null;
  const p = dados.plataforma;

  return (
    <Kpis aria-label="Números da plataforma">
      <div className="grupo">
        <h3>Comunidade e conteúdo</h3>
        <div className="grade">
          <Kpi valor={p.grupos} rotulo={p.subgrupos > 0 ? `Grupos (${p.subgrupos} ${p.subgrupos === 1 ? "subgrupo" : "subgrupos"})` : "Grupos"} />
          <Kpi valor={p.postagens} rotulo="Publicações" />
          <Kpi valor={p.comentarios} rotulo="Comentários" />
          <Kpi valor={p.especies} rotulo="Espécies cadastradas" />
          <Kpi valor={p.biomas} rotulo="Biomas" />
          <Kpi valor={p.organizacoes} rotulo="Organizações" />
          <Kpi valor={p.apoiadores} rotulo="Apoiadores ativos" />
          <Kpi valor={p.patrocinadores} rotulo="Banners de patrocinadores" />
        </div>
      </div>
    </Kpis>
  );
}

// -------------------------------------------------------------------- Recentes

const ABAS_RECENTES = [
  { id: "usuarios", rotulo: "Usuários", ver: "/admin/usuarios" },
  { id: "plantios", rotulo: "Plantios", ver: null },
  { id: "pedidos", rotulo: "Pedidos de perfil", ver: "/admin/pedidos" },
];

const classeStatus = (status) => (status || "").split(" ")[0];

// Colunas por aba. Cada aba mostra UMA tabela de largura total, no mesmo padrão
// da lista de usuários.
const COLUNAS = {
  usuarios: [
    { id: "nome", rotulo: "Usuário", largura: "minmax(200px, 2fr)",
      celula: (u) => <div><div className="principal">{u.nome}</div><div className="meta">{u.email}</div></div> },
    { id: "local", rotulo: "Localização", largura: "minmax(150px, 1.5fr)", celula: (u) => <span>{localDe(u.cidade, u.estado)}</span> },
    { id: "perfil", rotulo: "Perfil", largura: "120px", celula: (u) => <span className={`selo tipo-${u.tipo}`}>{u.tipo}</span> },
    { id: "status", rotulo: "Status", largura: "170px", celula: (u) => <span className={`selo status-${classeStatus(u.status)}`}>{u.status}</span> },
    { id: "data", rotulo: "Cadastrado em", largura: "120px", direita: true, celula: (u) => <span className="data">{dataCurta(u.createdAt)}</span> },
  ],
  plantios: [
    { id: "titulo", rotulo: "Plantio", largura: "minmax(200px, 2fr)",
      celula: (p) => <div className="principal">{p.titulo || "Plantio sem título"}</div> },
    { id: "quem", rotulo: "Registrado por", largura: "minmax(150px, 1.5fr)", celula: (p) => <span>{p.usuario_nome || "Usuário removido"}</span> },
    { id: "categoria", rotulo: "Categoria", largura: "120px",
      celula: (p) => <span className={`selo ${p.tipo_categoria === "Profissional" ? "tipo-Profissional" : "tipo-Comum"}`}>{p.tipo_categoria === "Profissional" ? "Área" : "Individual"}</span> },
    { id: "tipo", rotulo: "Tipo", largura: "170px", celula: (p) => <span>{p.tipo}</span> },
    { id: "local", rotulo: "Localização", largura: "minmax(150px, 1.5fr)", celula: (p) => <span>{localDe(p.cidade, p.estado)}</span> },
    { id: "data", rotulo: "Registrado em", largura: "120px", direita: true, celula: (p) => <span className="data">{dataCurta(p.createdAt)}</span> },
  ],
  pedidos: [
    { id: "nome", rotulo: "Solicitante", largura: "minmax(200px, 2fr)",
      celula: (p) => <div><div className="principal">{p.usuario_nome}</div><div className="meta">{p.usuario_email}</div></div> },
    { id: "atuacao", rotulo: "Atuação", largura: "minmax(180px, 2fr)",
      celula: (p) => <div><div>{p.tipo_atuacao}</div>{p.organizacao && <div className="meta">{p.organizacao}</div>}</div> },
    { id: "status", rotulo: "Situação", largura: "170px", celula: (p) => <span className={`selo ${p.status}`}>{p.status}</span> },
    { id: "data", rotulo: "Pedido em", largura: "120px", direita: true, celula: (p) => <span className="data">{dataCurta(p.createdAt)}</span> },
  ],
};

export function BlocoRecentes() {
  const { dados, erro, carregando } = useRelatorio(Relatorio.recentes, { limite: 6 });
  const [aba, setAba] = useState("usuarios");
  const atual = ABAS_RECENTES.find((a) => a.id === aba);
  const linhas = dados ? dados[aba] : [];

  return (
    <Secao aria-labelledby="titulo-recentes">
      <div className="cabecalho-bloco">
        <h3 id="titulo-recentes">Atividade recente</h3>
        {atual.ver && <Link to={atual.ver} className="ver-todos">Ver todos</Link>}
      </div>

      <Abas abas={ABAS_RECENTES} ativa={aba} onChange={setAba} idBase="recentes" rotulo="Atividade recente" />

      {carregando && !dados && <Esqueleto variante="linha" quantidade={4} rotulo="Carregando atividade" />}
      {erro && <div className="erro" role="alert">{erro}</div>}

      {dados &&
        <Recentes role="tabpanel" id={`recentes-painel-${aba}`} aria-labelledby={`recentes-${aba}`}>
          <TabelaAdmin
            rotulo={`Atividade recente: ${atual.rotulo}`}
            colunas={COLUNAS[aba]}
            linhas={linhas}
            chave={(l) => `${aba}-${l.id}`}
            larguraMinima={760}
            vazio={aba === "usuarios" ? "Nenhum usuário ainda." : aba === "plantios" ? "Nenhum plantio ainda." : "Nenhum pedido ainda."}
          />
        </Recentes>
      }
    </Secao>
  );
}
